import { COLUMN_SELECT_ID } from "@/constants";
import { Table } from "@tanstack/react-table";
import { useCallback, useEffect, useRef } from "react";
import {
  getCellAddressFromEvent,
  getCellAddressFromPoint,
  isInsideTable,
} from "../utils/cellAddress";
import useAutoScroll from "./useAutoScroll";
import useCopyToClipboard from "./useCopyToClipboard";
import { useCreateCopySelectionStore } from "./useCreateCopySelectionStore";
import useSelectionRange from "./useSelectionRange";

function isInteractiveElement(element: HTMLElement | null): boolean {
  if (!element) return false;
  return !!element.closest(
    "input, textarea, select, button, a, [contenteditable]",
  );
}

interface UseTableCopySelectionParams<TData> {
  enabled: boolean;
  table: Table<TData>;
  visibleColumnOrder: string[];
  containerRef: React.RefObject<HTMLElement | null>;
  scrollElementRef: React.RefObject<HTMLElement | null>;
  horizontalScrollElementRef?: React.RefObject<HTMLElement | null>;
}

export default function useTableCopySelection<TData>({
  enabled,
  table,
  visibleColumnOrder,
  containerRef,
  scrollElementRef,
  horizontalScrollElementRef,
}: UseTableCopySelectionParams<TData>) {
  const store = useCreateCopySelectionStore();

  const {
    setStart,
    setEnd,
    getStart,
    getEnd,
    setSelecting,
    isSelecting,
    updateRange,
    clearSelection,
    getSelectionBounds,
  } = useSelectionRange({ visibleColumnOrder, store });

  // Ref to hold the update function to avoid circular dependency
  const updateSelectionRef = useRef<(() => void) | null>(null);

  const autoScroll = useAutoScroll({
    scrollElementRef,
    horizontalScrollElementRef,
    onScroll: () => updateSelectionRef.current?.(),
  });

  // Update selection based on current mouse position
  const updateSelectionFromMousePosition = useCallback(() => {
    const mousePos = autoScroll.getMousePosition();
    const start = getStart();
    if (!mousePos || !start) {
      return;
    }

    let { clientX, clientY } = mousePos;

    const horizontalElement =
      horizontalScrollElementRef?.current ?? scrollElementRef.current;
    const verticalElement = scrollElementRef.current;

    if (horizontalElement) {
      const left =
        horizontalElement.getBoundingClientRect().left +
        horizontalElement.clientLeft;
      const right = left + horizontalElement.clientWidth;
      clientX = Math.max(left + 1, Math.min(right - 1, clientX));
    }

    if (verticalElement) {
      const top =
        verticalElement.getBoundingClientRect().top + verticalElement.clientTop;
      const bottom = top + verticalElement.clientHeight;
      clientY = Math.max(top + 1, Math.min(bottom - 1, clientY));
    }

    const address = getCellAddressFromPoint(clientX, clientY);

    if (!address || address.columnId === COLUMN_SELECT_ID) {
      return;
    }

    const prev = getEnd();

    if (
      prev &&
      prev.rowIndex === address.rowIndex &&
      prev.columnId === address.columnId
    ) {
      return;
    }

    setEnd(address);
    updateRange(start, address);
  }, [
    autoScroll,
    scrollElementRef,
    horizontalScrollElementRef,
    getStart,
    getEnd,
    setEnd,
    updateRange,
  ]);

  // Keep ref in sync via effect to avoid stale closures in concurrent mode
  useEffect(() => {
    updateSelectionRef.current = updateSelectionFromMousePosition;
  });

  const { handleCopy, buildSelectionData } = useCopyToClipboard({
    table,
    visibleColumnOrder,
    getSelectionBounds,
  });

  // Refs for document handlers
  const documentMouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const documentMouseUpRef = useRef<(() => void) | null>(null);

  const cleanupDocumentListeners = useCallback(() => {
    if (documentMouseMoveRef.current) {
      document.removeEventListener("mousemove", documentMouseMoveRef.current);
      documentMouseMoveRef.current = null;
    }
    if (documentMouseUpRef.current) {
      document.removeEventListener("mouseup", documentMouseUpRef.current);
      documentMouseUpRef.current = null;
    }
    autoScroll.stopAutoScroll();
  }, [autoScroll]);

  const handleMouseDown = useCallback(
    (event: Event) => {
      const mouseEvent = event as MouseEvent;
      if (mouseEvent.button !== 0) return; // only left click

      const target = mouseEvent.target as HTMLElement;

      // Don't start selection if clicking on interactive/focusable elements
      // This allows these elements to receive focus and handle clicks normally
      if (isInteractiveElement(target)) {
        return;
      }

      clearSelection();

      if (!isInsideTable(target)) return;

      const address = getCellAddressFromEvent(mouseEvent);
      if (!address) return;
      if (address.columnId === COLUMN_SELECT_ID) return;

      setStart(address);
      setEnd(address);
      setSelecting(true);
      autoScroll.setActive(true);
      autoScroll.updateMousePosition(mouseEvent.clientX, mouseEvent.clientY);

      // Create document-level handlers for tracking mouse outside the container
      const handleDocumentMouseMove = (e: MouseEvent) => {
        if (!isSelecting()) return;

        autoScroll.updateMousePosition(e.clientX, e.clientY);

        if (autoScroll.checkNeedsScroll(e.clientX, e.clientY)) {
          autoScroll.startAutoScroll();
        } else {
          autoScroll.stopAutoScroll();
        }

        updateSelectionFromMousePosition();
      };

      const handleDocumentMouseUp = () => {
        setSelecting(false);
        autoScroll.setActive(false);
        cleanupDocumentListeners();
        containerRef.current?.focus({ preventScroll: true });
      };

      documentMouseMoveRef.current = handleDocumentMouseMove;
      documentMouseUpRef.current = handleDocumentMouseUp;
      document.addEventListener("mousemove", handleDocumentMouseMove);
      document.addEventListener("mouseup", handleDocumentMouseUp);
    },
    [
      containerRef,
      autoScroll,
      clearSelection,
      setStart,
      setEnd,
      setSelecting,
      isSelecting,
      updateSelectionFromMousePosition,
      cleanupDocumentListeners,
    ],
  );

  const abortSelection = useCallback(() => {
    clearSelection();
    cleanupDocumentListeners();
  }, [clearSelection, cleanupDocumentListeners]);

  // Clear selection when clicking outside the container
  useEffect(() => {
    if (!enabled) return;

    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (!store.hasSelection()) return;
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        clearSelection();
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [enabled, containerRef, clearSelection, store]);

  // Prevent native text selection during active drag
  const handleSelectStart = useCallback(
    (event: Event) => {
      if (store.hasSelection()) {
        event.preventDefault();
      }
    },
    [store],
  );

  const copySelectionToClipboard = useCallback(() => {
    const data = buildSelectionData();
    if (!data) return;

    navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([data.tsv], { type: "text/plain" }),
        "text/html": new Blob([data.html], { type: "text/html" }),
      }),
    ]);
  }, [buildSelectionData]);

  // Attach event listeners
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("copy", handleCopy);
    container.addEventListener("selectstart", handleSelectStart);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("copy", handleCopy);
      container.removeEventListener("selectstart", handleSelectStart);
      cleanupDocumentListeners();
    };
  }, [
    enabled,
    containerRef,
    handleMouseDown,
    handleCopy,
    handleSelectStart,
    cleanupDocumentListeners,
  ]);

  // Reset selection when the visible row set changes (filtering, pagination, data refresh).
  // Uses row count as a stable proxy — avoids false clears from unstable data array references.
  const rowCount = table.getRowModel().rows.length;
  useEffect(() => {
    clearSelection();
    cleanupDocumentListeners();
  }, [rowCount, clearSelection, cleanupDocumentListeners]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupDocumentListeners();
    };
  }, [cleanupDocumentListeners]);

  return { store, abortSelection, copySelectionToClipboard };
}
